import type { ArrayBinding } from '@doer/frontend-components/creativity-editor';
import type { Binder } from './binder';
import { expandPieceIdPattern } from '../core/piece-id';
import { setAtPath } from '../core/binding-map';
import { resolveValuePath } from '../core/value-path';

const isNonEmpty = (v: unknown): boolean => v != null && v !== '';
const pickAt = (arr: unknown[], i: number, valuePath?: string): unknown =>
  i < arr.length ? resolveValuePath(arr[i], valuePath) : undefined;

// Accepts an array or a `{ <resolutionId>: [...] }` map.
function resolveArrayDefaults(raw: unknown, resolutionId?: string): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (raw != null && typeof raw === 'object' && resolutionId) {
    const candidate = (raw as Record<string, unknown>)[resolutionId];
    return Array.isArray(candidate) ? candidate : [];
  }
  return [];
}

export const arrayBinder: Binder<ArrayBinding> = {
  resolveTargets(binding, formData, defaults, resolutionId) {
    const formArr = Array.isArray(formData[binding.fieldKey]) ? (formData[binding.fieldKey] as unknown[]) : [];
    const defaultArr = resolveArrayDefaults(defaults[binding.fieldKey], resolutionId);

    return Array.from({ length: binding.maxSlots }, (_, i) => {
      const fromForm = pickAt(formArr, i, binding.valuePath);
      const value = isNonEmpty(fromForm) ? fromForm : pickAt(defaultArr, i, binding.valuePath);
      return {
        pieceId: expandPieceIdPattern(binding.pieceIdPattern, i),
        value,
        bindingType: binding.bindingType
      };
    });
  },

  pieceMapEntries(binding) {
    const entries: Array<[string, ReturnType<Binder<ArrayBinding>['pieceMapEntries']>[number][1]]> = [];
    for (let i = 0; i < binding.maxSlots; i++) {
      entries.push([
        expandPieceIdPattern(binding.pieceIdPattern, i),
        {
          fieldKey: binding.fieldKey,
          kind: 'array',
          bindingType: binding.bindingType,
          index: i,
          valuePath: binding.valuePath
        }
      ]);
    }
    return entries;
  },

  computeNextFieldValue(entry, formData, newValue) {
    if (entry.kind !== 'array') return newValue;
    const current = formData[entry.fieldKey];
    const arr = Array.isArray(current) ? [...current] : [];
    while (arr.length <= entry.index) arr.push(undefined);

    if (!entry.valuePath) {
      arr[entry.index] = newValue;
    } else {
      const slot = arr[entry.index];
      const base = slot != null && typeof slot === 'object' ? (slot as Record<string, unknown>) : {};
      arr[entry.index] = setAtPath(base, entry.valuePath, newValue);
    }
    return arr;
  }
};
